import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api.ts';
import { KIND_LABELS, type CatalogItem, type Kind } from '../../catalog.ts';

interface AdminItem extends CatalogItem {
  usage: { extras: number; missions: number };
}

interface Data {
  kinds: Kind[];
  items: AdminItem[];
  roleSkills: Record<string, string[]>;
}

const ERRORS: Record<string, string> = {
  ALREADY_EXISTS: 'Un élément de ce type porte déjà ce nom.',
  MISSING_FIELDS: 'Le libellé est obligatoire.',
  IN_USE: 'Cet élément est utilisé : désactivez-le plutôt que de le supprimer.',
  INVALID_SKILLS: 'Sélection de compétences invalide.',
};

const GROUPED: Kind[] = ['ROLE', 'CERTIFICATION', 'EQUIPMENT'];

export function AdminCatalog() {
  const [data, setData] = useState<Data | null>(null);
  const [kind, setKind] = useState<Kind>('ROLE');
  const [label, setLabel] = useState('');
  const [group, setGroup] = useState('');
  const [editing, setEditing] = useState<{ id: string; label: string; group: string } | null>(null);
  const [skillsFor, setSkillsFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<Data>('/admin/catalog').then(setData);
  }, []);
  useEffect(load, [load]);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      load();
    } catch (err) {
      setError(err instanceof ApiError ? ERRORS[err.code] ?? 'Action impossible.' : 'Action impossible.');
    }
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    await run(async () => {
      await api.post('/admin/catalog', { kind, label, group: group || undefined });
      setLabel('');
    });
  }

  if (!data) return <div className="empty-state">Chargement…</div>;
  const items = data.items.filter((i) => i.kind === kind);
  const skills = data.items.filter((i) => i.kind === 'HARD_SKILL' || i.kind === 'SOFT_SKILL');
  const roleBeingEdited = data.items.find((i) => i.id === skillsFor);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Le référentiel alimente le profil des extras et les exigences des missions. Un élément déjà choisi par un extra ou exigé par une mission se désactive (il disparaît des formulaires sans rien casser) ; il ne se supprime que s'il n'est utilisé nulle part.
      </div>
      {error && <div className="error-banner">{error}</div>}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {data.kinds.map((k) => (
          <button key={k} className={`admin-nav-link${kind === k ? ' active' : ''}`} style={{ border: 'none', cursor: 'pointer' }} onClick={() => { setKind(k); setSkillsFor(null); setEditing(null); }}>
            {KIND_LABELS[k]} ({data.items.filter((i) => i.kind === k).length})
          </button>
        ))}
      </div>

      <form onSubmit={add} className="card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ flex: 2, minWidth: 220 }} required placeholder={`Ajouter dans « ${KIND_LABELS[kind]} »`} value={label} onChange={(e) => setLabel(e.target.value)} />
        {GROUPED.includes(kind) && <input className="input" style={{ flex: 1, minWidth: 140 }} placeholder="Groupe (facultatif)" value={group} onChange={(e) => setGroup(e.target.value)} />}
        <button className="btn btn-primary btn-sm">Ajouter</button>
      </form>

      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th>Libellé</th>{GROUPED.includes(kind) && <th>Groupe</th>}<th>Utilisation</th><th>Statut</th><th /></tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} style={{ opacity: i.active ? 1 : 0.55 }}>
                <td>
                  {editing?.id === i.id ? (
                    <input className="input" value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} aria-label="Libellé" />
                  ) : (
                    <span style={{ fontWeight: 700 }}>{i.label}</span>
                  )}
                </td>
                {GROUPED.includes(kind) && (
                  <td>{editing?.id === i.id ? <input className="input" value={editing.group} onChange={(e) => setEditing({ ...editing, group: e.target.value })} aria-label="Groupe" /> : i.group ?? '—'}</td>
                )}
                <td style={{ fontSize: 12.5 }}>{i.usage.extras} extra(s) · {i.usage.missions} mission(s)</td>
                <td><span className={`badge ${i.active ? 'badge-available' : 'badge-neutral'}`}>{i.active ? 'Actif' : 'Désactivé'}</span></td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {editing?.id === i.id ? (
                    <>
                      <button className="btn btn-primary btn-sm" onClick={() => run(async () => { await api.patch(`/admin/catalog/${i.id}`, { label: editing.label, ...(GROUPED.includes(kind) ? { group: editing.group } : {}) }); setEditing(null); })}>Enregistrer</button>{' '}
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Annuler</button>
                    </>
                  ) : (
                    <>
                      {kind === 'ROLE' && <button className="btn btn-secondary btn-sm" onClick={() => setSkillsFor(skillsFor === i.id ? null : i.id)}>Compétences ({data.roleSkills[i.id]?.length ?? 0})</button>}{' '}
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing({ id: i.id, label: i.label, group: i.group ?? '' })}>Renommer</button>{' '}
                      <button className="btn btn-secondary btn-sm" onClick={() => run(() => api.patch(`/admin/catalog/${i.id}`, { active: !i.active }))}>{i.active ? 'Désactiver' : 'Réactiver'}</button>{' '}
                      <button className="btn btn-danger-outline btn-sm" disabled={i.usage.extras + i.usage.missions > 0} title={i.usage.extras + i.usage.missions > 0 ? 'Utilisé : désactivez-le' : undefined} onClick={() => window.confirm(`Supprimer « ${i.label} » ?`) && run(() => api.delete(`/admin/catalog/${i.id}`))}>Supprimer</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {roleBeingEdited && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontWeight: 800 }}>Compétences proposées pour « {roleBeingEdited.label} »</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 6 }}>
            {skills.map((s) => {
              const on = (data.roleSkills[roleBeingEdited.id] ?? []).includes(s.id);
              return (
                <label key={s.id} style={{ display: 'flex', gap: 8, fontSize: 13, alignItems: 'flex-start', cursor: 'pointer', opacity: s.active ? 1 : 0.55 }}>
                  <input
                    type="checkbox"
                    checked={on}
                    style={{ marginTop: 3 }}
                    onChange={() => {
                      const current = data.roleSkills[roleBeingEdited.id] ?? [];
                      const next = on ? current.filter((x) => x !== s.id) : [...current, s.id];
                      run(() => api.put(`/admin/catalog/roles/${roleBeingEdited.id}/skills`, { skillIds: next }));
                    }}
                  />
                  <span>{s.label} <span style={{ color: 'var(--text-tertiary)' }}>· {s.kind === 'HARD_SKILL' ? 'technique' : 'savoir-être'}</span></span>
                </label>
              );
            })}
          </div>
          <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setSkillsFor(null)}>Fermer</button>
        </div>
      )}
    </div>
  );
}
